import { Link } from "react-router-dom";
import { Sparkles, Heart, Flame, Wind, Sunrise, Scale, Target, ArrowRight, Trophy, MessageCircleHeart, Globe, Puzzle, Shield, Repeat, Sprout, Compass, Waves, UserCog, Feather, Star, Anchor, Apple, Handshake, HeartHandshake, Ear, Megaphone, MessagesSquare, LifeBuoy } from "lucide-react";
import { MODULES } from "../data/modules";

// Official WAIT Counseling website
const WEBSITE_URL = "https://waitcounseling.org";

// Map icon names (from the canonical modules data) to lucide components.
const ICONS = { Heart, Flame, Wind, Sunrise, Scale, Target, Puzzle, Shield, Repeat, Sprout, Compass, Waves, UserCog, Feather, Star, Anchor, Apple, Handshake, HeartHandshake, Ear, Megaphone, MessagesSquare, LifeBuoy };

// Render all programs alphabetically by display name (single source of truth).
const sortedModules = [...MODULES].sort((a, b) => a.name.localeCompare(b.name));

// Log which program card a visitor taps on the public landing page.
const handleModuleClick = (key) => {
  console.info(`landing_click: ${key}`);
};

export default function Landing() {
  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-2xl bg-[#E8A87C] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-head font-extrabold text-lg">WAIT <span className="text-[#E8A87C]">Mental Health</span></span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold hover:opacity-70 transition-opacity" data-testid="landing-login-link">
            Log in
          </Link>
          <Link
            to="/register"
            className="bg-[#2D3748] text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:-translate-y-0.5 transition-transform"
            data-testid="landing-register-link"
          >
            Get started
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-10 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-[#FCF3EB] text-[#B45309] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <MessageCircleHeart className="w-4 h-4" /> Guided, rewarding wellbeing
          </div>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Small steps.
            <br />
            <span className="text-[#E8A87C]">Real progress.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-md">
            The <b className="text-foreground">WAIT Mental Health App</b> offers interactive therapy
            exercises across {MODULES.length} focused programs — including couples & marriage, anger, anxiety,
            depression, bipolar, ADHD, autism, PTSD, OCD, substance recovery, grief, self-esteem,
            eating disorder support and more. Answer, learn and earn encouraging rewards as you build
            healthier habits, one gentle question at a time.
          </p>
          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-[#E8A87C] hover:opacity-70 transition-opacity"
            data-testid="landing-website-link"
          >
            <Globe className="w-4 h-4" /> Visit our website
          </a>
          <div className="mt-8 flex items-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#E8A87C] text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform shadow-sm hover:shadow-md"
              data-testid="landing-cta"
            >
              Begin your journey <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
              <Trophy className="w-4 h-4" style={{ color: "#D4AF37" }} /> Points · Streaks · Badges
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          {sortedModules.map((m) => {
            const Icon = ICONS[m.icon] || Sparkles;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => handleModuleClick(m.key)}
                className="text-left rounded-3xl p-6 border border-black/5 shadow-sm hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: m.bg }}
                data-testid={`landing-module-${m.key}`}
              >
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: m.color }}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <p className="font-head font-bold text-[15px] leading-tight">{m.name}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">{m.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 py-10 text-sm text-muted-foreground border-t border-black/5">
        The WAIT Mental Health App is a supportive self-help tool and is not a substitute for
        professional care. In a crisis, contact your local emergency services. Learn more at{" "}
        <a
          href={WEBSITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#E8A87C] hover:opacity-70 transition-opacity"
          data-testid="footer-website-link"
        >
          our website
        </a>
        .
      </footer>
    </div>
  );
}
